// Create game (UC-08). Organizer picks court, time, format, skill, capacity.
// On success we jump straight to the new game's detail, where "find players"
// (Flow 3, the demo centerpiece) is one tap away.

import $ from 'jquery';
import type { Court } from '@shared/models';
import type { GameFormat, GameVisibility, SkillLevel } from '@shared/models';
import type { Screen } from './types';
import { api } from '../api';
import { navigate } from '../router';
import { esc } from '../ui/dom';
import { FORMAT_TR, SKILL_TR } from '../ui/format';
import { errorHtml, loadingHtml } from '../ui/states';
import { toast } from '../ui/toast';

const FORMATS: GameFormat[] = ['3v3', '4v4', '5v5', 'pickup'];
const SKILLS: SkillLevel[] = ['beginner', 'intermediate', 'advanced'];

// Sensible default capacity per format; pickup gets a roomy 10.
const CAPACITY_FOR: Record<GameFormat, number> = {
  '3v3': 6,
  '4v4': 8,
  '5v5': 10,
  pickup: 10,
};

/** Local datetime-local value for tomorrow at 20:00. */
function defaultStart(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(20, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

export const CreateScreen: Screen = async (body) => {
  body.html(loadingHtml());
  let courts: Court[];
  try {
    courts = await api.call('courts.list', undefined);
  } catch (err) {
    body.html(errorHtml(err instanceof Error ? err.message : String(err)));
    return;
  }

  const courtOpts = courts
    .map((c) => `<option value="${esc(c.id)}">${esc(c.name)} · ${esc(c.area)}</option>`)
    .join('');
  const formatChips = FORMATS.map(
    (f, i) => `<button type="button" class="chip ${i === 2 ? 'is-on' : ''}" data-format="${f}">${FORMAT_TR[f]}</button>`,
  ).join('');
  const skillOpts = SKILLS.map(
    (s) => `<option value="${s}" ${s === 'intermediate' ? 'selected' : ''}>${SKILL_TR[s]}</option>`,
  ).join('');

  body.html(`
    <div class="create">
      <label class="field">
        <span class="field__label">Saha</span>
        <select class="select" id="c-court">${courtOpts}</select>
      </label>

      <label class="field">
        <span class="field__label">Tarih ve saat</span>
        <input class="input" id="c-when" type="datetime-local" value="${defaultStart()}" />
      </label>

      <div class="field">
        <span class="field__label">Format</span>
        <div class="chips" id="c-formats">${formatChips}</div>
      </div>

      <label class="field">
        <span class="field__label">Seviye</span>
        <select class="select" id="c-skill">${skillOpts}</select>
      </label>

      <label class="field">
        <span class="field__label">Kadro (kaç kişi)</span>
        <input class="input" id="c-capacity" type="number" min="2" max="12" value="10" />
      </label>

      <div class="field">
        <span class="field__label">Görünürlük</span>
        <div class="vis" id="c-visibility">
          <button type="button" class="vis__opt is-on" data-vis="private">
            <span class="vis__title">🔒 Sadece davetliler</span>
            <span class="vis__desc">Yalnızca davet ettiğin kişiler görür ve katılır.</span>
          </button>
          <button type="button" class="vis__opt" data-vis="public">
            <span class="vis__title">🌐 Herkese açık</span>
            <span class="vis__desc">Yakındaki herkes bulabilir ve katılabilir.</span>
          </button>
        </div>
      </div>

      <label class="field field--row">
        <input type="checkbox" id="c-plays" checked />
        <span class="field__label" style="margin:0">Ben de oynayacağım</span>
      </label>

      <button class="btn btn--primary btn--block" id="c-create">Oyunu oluştur</button>
    </div>
  `);

  // Format is single-select; picking one also updates the default capacity.
  let format: GameFormat = '5v5';
  body.on('click', '#c-formats .chip', function () {
    format = $(this).data('format') as GameFormat;
    $('#c-formats .chip').removeClass('is-on');
    $(this).addClass('is-on');
    $('#c-capacity').val(CAPACITY_FOR[format]);
  });

  // Visibility is single-select; invite-only by default.
  let visibility: GameVisibility = 'private';
  body.on('click', '#c-visibility .vis__opt', function () {
    visibility = $(this).data('vis') as GameVisibility;
    $('#c-visibility .vis__opt').removeClass('is-on');
    $(this).addClass('is-on');
  });

  $('#c-create').on('click', async () => {
    const $btn = $('#c-create');
    const whenLocal = $('#c-when').val() as string;
    if (!whenLocal) {
      toast('Tarih ve saat seç', 'error');
      return;
    }
    const capacity = Math.max(2, Math.min(12, Number($('#c-capacity').val()) || CAPACITY_FOR[format]));

    $btn.prop('disabled', true).text('Oluşturuluyor…');
    try {
      const detail = await api.call('games.create', {
        courtId: $('#c-court').val() as string,
        startsAt: new Date(whenLocal).toISOString(),
        format,
        skill: $('#c-skill').val() as SkillLevel,
        capacity,
        organizerPlays: $('#c-plays').is(':checked'),
        visibility,
      });
      toast('Oyun oluşturuldu 🏀', 'success');
      navigate(`/game/${detail.game.id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
      $btn.prop('disabled', false).text('Oyunu oluştur');
    }
  });
};
